import cn from "classnames";
import Link from "next/link";
import Image from "next/image";

import { BaseButton } from "@/src/component/base/BaseButton";
import { BrowserHooks } from "@/src/hook";
import { useCMS } from "@/src/hook/provider";
import { FrontendTrackingService } from "@/src/service";
import type { PostTrackingLogInput } from "@/src/service";
import { StrapiComponentType } from "@/src/type";
import { StringHelper } from "@/src/util";

import styles from "./HeaderButton.module.scss";

interface ButtonLinkModuleProps {
  buttonLink: StrapiComponentType.Element.ButtonLink;
  tracking?: PostTrackingLogInput;
  darkMenuIcon?: boolean;
  className?: string;
  alwaysShowIcon?: boolean;
  hideOverride?: "off" | "on" | "offBelowMD" | "offBelowSM";
}

function ButtonLinkModule({ buttonLink, tracking, darkMenuIcon, className, alwaysShowIcon, hideOverride }: ButtonLinkModuleProps) {
  const cms = useCMS();
  const externalLink = BrowserHooks.useLinkWithTrackingParams(buttonLink.href, buttonLink.forwardMarketingParams ?? false);
  const iconAttributes = darkMenuIcon ? buttonLink.iconDark?.data?.attributes : buttonLink.iconLight?.data?.attributes;
  const hasImage = iconAttributes?.url && StringHelper.checkIsStringOrIsNull(buttonLink.showIconBreakpoint, "off") !== "off";

  /**
   * if the link provided is external do not use the CMS link generator
   */
  function getHref(): string {
    return buttonLink.isExternal ? externalLink : cms.constructLink(buttonLink.href);
  }

  /**
   * track the button click
   */
  function handleClick(e: any) {
    FrontendTrackingService.postTrackingLog(
      tracking ?? {
        action: "click",
        actionId: buttonLink.trackingEvent?.gaEventName ?? buttonLink.href,
        actionIdType: buttonLink.trackingEvent?.gaEventName ? "custom_event" : "href",
        actionMessage: `button: ${buttonLink.name}`,
        triggeredFrom: "ButtonLinkModule.tsx",
        pushTo: {
          hubspot: true,
          googleAnalytics: buttonLink.trackingEvent?.gaEventName,
          meta: buttonLink.trackingEvent?.fbEventName,
          googleAds: buttonLink.trackingEvent?.googleAdsConversion,
        },
      }
    );

    const link = getHref();
    const isAnchorLink = link.includes(window.location.pathname) && !link.includes("#");
    if (!isAnchorLink) {
      e.preventDefault();
      e.stopPropagation();
      window.open(getHref(), buttonLink.target ?? "self");
    }
  }

  function getShowIconBreakpoint() {
    switch (hideOverride ?? buttonLink.showIconBreakpoint ?? "off") {
      case "belowLG":
        return styles.belowLG;
      case "belowMD":
        return styles.belowMD;
      case "belowSM":
        return styles.belowSM;
      case "offBelowMD":
        return styles.offBelowMD;
      case "offBelowSM":
        return styles.offBelowSM;
      default:
        return undefined;
    }
  }

  return (
    <Link
      className={className}
      href={getHref()}
      target={buttonLink.isExternal ? "_blank" : "_self"}
      shallow={(getHref() ?? "").includes("?section=")}
      onClick={handleClick}
      prefetch={!buttonLink.isExternal}
    >
      <BaseButton
        className={cn(styles.ctaLargeScreen, hasImage ? styles.icon : styles.buttonWithText, getShowIconBreakpoint())}
        title={buttonLink.label}
        color={darkMenuIcon ? "primary" : "secondary"}
        variant={buttonLink.variant ?? "outlined"}
        size={buttonLink.size ?? "large"}
        sx={{
          width: buttonLink.width ? `${buttonLink.width}px` : undefined,
        }}
        aria-label={buttonLink.label}
        endIcon={
          hasImage && alwaysShowIcon ? (
            <Image
              src={iconAttributes?.url ?? ""}
              width={iconAttributes?.width ?? 30}
              height={iconAttributes?.height ?? 30}
              alt={`${buttonLink.label} Icon Button`}
              priority={true}
            />
          ) : undefined
        }
      >
        {buttonLink.label}
      </BaseButton>
      {hasImage && (
        <Image
          className={cn(styles.ctaSmallScreen, styles.icon, getShowIconBreakpoint())}
          src={iconAttributes?.url ?? ""}
          width={iconAttributes?.width ?? 30}
          height={iconAttributes?.height ?? 30}
          alt={`${buttonLink.label} Icon Button`}
          priority={true}
        />
      )}
    </Link>
  );
}

export default ButtonLinkModule;
